import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { matchingPlan, planTravel, planChecklist } from "@/lib/memberPlans";

const REFUSAL = { 401: "Bitte melde dich erneut an.", 403: "Dafür fehlt dir die Berechtigung.",
  404: "Dieser Plan ist nicht mehr verfügbar.", 409: "Der Plan wurde inzwischen geändert. Bitte erneut laden.",
  422: "Die Angaben wurden nicht angenommen. Bitte prüfe sie.", 429: "Bitte warte einen Moment und versuche es erneut." };

// One explicit PUT per click. A lost completion retains its UUID; checking never writes.
export function usePlanSave(path, section) {
  const [state, setState] = useState({ path, phase: "idle", confirmed: null, reason: "" });
  const mounted = useRef(true), current = useRef(path), flight = useRef(null), operation = useRef(null), epoch = useRef(0);
  if (current.current !== path) { current.current = path; epoch.current += 1; operation.current = null; }
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const visible = state.path === path ? state : { path, phase: "idle", confirmed: null, reason: "" };
  const parse = section === "travel" ? planTravel : planChecklist;
  const active = () => mounted.current && current.current === path;
  const publish = (next) => { if (active()) setState((s) => ({ ...(s.path === path ? s : { confirmed: null }), path, ...next })); };
  const run = async (mode, payload) => {
    if (!path || (flight.current?.path === path && flight.current.epoch === epoch.current)) return;
    const generation = epoch.current;
    const stillHere = () => active() && epoch.current === generation;
    let op = operation.current;
    if (mode === "save" && (!op || op.path !== path || JSON.stringify(op.payload) !== JSON.stringify(payload))) {
      if (typeof globalThis.crypto?.randomUUID !== "function") { publish({ phase: "rejected", reason: "Speichern ist in diesem Browser gerade nicht möglich." }); return; }
      op = { path, payload, key: globalThis.crypto.randomUUID(), createdAt: Date.now(), accepted: false };
      operation.current = op;
    }
    if (!op || op.path !== path) return;
    // Core receipts last 24 hours. A long-open draft may check, but never replay beyond that window.
    if (mode === "save" && Date.now() - op.createdAt >= 24 * 60 * 60 * 1000) {
      publish({ phase: "unclear", reason: "Der Wiederholungszeitraum ist abgelaufen. Bitte lade den Plan neu und prüfe deine Angaben." });
      return;
    }
    const token = { path, epoch: generation }; flight.current = token;
    publish({ phase: "saving", reason: "" });
    try {
      if (mode === "save") {
        try {
          const result = await api.put(path, op.payload, { headers: { "Idempotency-Key": op.key } });
          if (!stillHere()) return;
          try { op.accepted = result.status >= 200 && result.status < 300 && parse(result.data) !== null; } catch { op.accepted = false; }
        } catch (error) {
          if (!stillHere()) return;
          if (error.memberGuard || Object.prototype.hasOwnProperty.call(REFUSAL, error.response?.status)) {
            publish({ phase: "rejected", reason: error.memberGuard ? "Das Speichern ist in dieser Umgebung gesperrt." : REFUSAL[error.response.status] }); return;
          }
        }
      }
      if (!stillHere()) return;
      const response = await api.get(path);
      if (!stillHere()) return;
      const fresh = parse(response.data);
      if (matchingPlan(section, fresh, op.payload)) publish({ phase: op.accepted ? "confirmed" : "stored", confirmed: fresh });
      else publish({ phase: "unclear", reason: "Die geladenen Angaben bestätigen diese Änderung noch nicht." });
    } catch {
      if (stillHere()) publish({ phase: "unclear", reason: "Der Speicherstand konnte nicht bestätigt werden." });
    } finally {
      if (flight.current === token) flight.current = null;
    }
  };
  const reset = useCallback(() => {
    operation.current = null;
    if (mounted.current && current.current === path) setState((s) => ({ ...(s.path === path ? s : { confirmed: null }), path, phase: "idle", reason: "" }));
  }, [path]);
  return { ...visible, busy: visible.phase === "saving", save: (payload) => run("save", payload), check: () => run("check"), reset };
}
